import {
    DEVICE_ALARM_COOLDOWN_MINUTES,
    DEVICE_ALARMS_DEFAULT,
    deviceAlarmConditions,
} from './alertDeviceAlarms';
import { NO_UPLINK_FIELD } from './noUplink';

/**
 * Starting points offered when a new alert is created.
 *
 * These are SUGGESTIONS, not a product decision: every threshold below is an editable starting value
 * (the user sees and changes it in the editor), chosen only so that a first alert makes sense without
 * knowing the measurement names. Nobody should read them as a recommended setting for a device.
 */
export type PresetId = 'deviceAlarm' | 'pressure' | 'temperature' | 'battery' | 'noUplink' | 'custom';

export interface AlertPreset {
    id: PresetId;
    /** Plain condition JSON, as the API stores it; `[]` for "custom". */
    conditions: any[];
    /** Wait time between two alerts of one device; absent = the server default (24 h). */
    cooldownMinutes?: number;
}

const compare = (field: string, operator: 'less' | 'greater', comparisonValue: number) => ({
    conditionType: 'Comparison',
    field,
    operator,
    comparisonType: 'value',
    comparisonValue,
});

export const ALERT_PRESETS: AlertPreset[] = [
    // The device's own alarms (moved, overturned, sensor fault): reported the moment the device raises
    // them, not by guessing a threshold of the acceleration — that preset ("Y acceleration below
    // 7 m/s²") alarmed on ordinary hourly readings of a device standing on a wall or a little tilted.
    {
        id: 'deviceAlarm',
        conditions: deviceAlarmConditions(DEVICE_ALARMS_DEFAULT),
        cooldownMinutes: DEVICE_ALARM_COOLDOWN_MINUTES,
    },
    // Pressure below 10 bar.
    { id: 'pressure', conditions: [compare('pressure', 'less', 10)] },
    // Temperature outside 5 to 60 °C: below 5 OR above 60, one OR group (shown as "any condition").
    {
        id: 'temperature',
        conditions: [{
            conditionType: 'OrCondition',
            conditions: [compare('temperature', 'less', 5), compare('temperature', 'greater', 60)],
        }],
    },
    // Battery voltage below 3 V.
    { id: 'battery', conditions: [compare('batteryVoltage', 'less', 3)] },
    // No data for 14 days — the "days without uplink" rule (stands alone, saved as running).
    { id: 'noUplink', conditions: [compare(NO_UPLINK_FIELD, 'greater', 14)] },
    { id: 'custom', conditions: [] },
];
